<template>
  <div class="studio-chart-wrapper selectchart" :class="chartWrapperClasses">
    <div v-if="loading">
      <ScreenLoader />
    </div>
    <div v-else>
      <div v-if="sampleError" class="text-center">{{ sampleError }}</div>
      <div v-else>
        <div class='main-chart pa-0'> 
          <div class="chart-name-top">
            <h6>{{ chartName }}</h6>
          </div>
          <div :ref="chartId"></div>
        </div>
        <v-dialog v-model="isModalOpen" class="multi-chart-preview-div dashboard-chart-preview composition">
          <v-card class="position-relative studio-card studio-custom-card ">
            <v-card-text>
              
                <div class="chart-name-top w-100">
            <h6>{{ chartName }}</h6>
            <div class="modal-tab-div">
                <div v-if="chartType === 'Line Plot' || chartType === 'Scatter Plot'"
                  class="tab-div justify-space-between">
                  <p>X Axis Scale</p>
                  <v-btn-toggle v-model="isModalXLog" class="axis-toggle setHeight" mandatory
                    @update:modelValue="handleModalXScaleToggleChange">
                    <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                    <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                  </v-btn-toggle>
                </div>
                <div v-if="chartType === 'Line Plot' || chartType === 'Scatter Plot'"
                  class="tab-div  justify-space-between">
                  <p>Y Axis Scale</p>
                  <v-btn-toggle v-model="isModalYLog" class="axis-toggle setHeight" mandatory
                    @update:modelValue="handleModalYScaleToggleChange">
                    <v-btn class="switch-btn" :value="true" outlined>Log</v-btn>
                    <v-btn class="switch-btn" :value="false" outlined>Linear</v-btn>
                  </v-btn-toggle>
                </div>
              </div>
          </div>
          <div :ref="modalChartId"></div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import ScreenLoader from "../Loaders/ScreenLoader.vue";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";

import downloadIcon from '../../../assets/Svg/Chart/csv_chart.svg';
import Expand from "../../../assets/Svg/Chart/expand-btn.svg";

import { eventBus } from "../../../eventBus.js";
import { mapGetters } from "vuex";
import { formatNumber, shouldFormatNumbers, createPlotlyTickFormat, createCustomTickFormatter } from "../../../utils/numberFormatter";
import { mergePlotlyTheme, applyPlotlyGridLineStroke } from "@/utils/chartTheme";
import { downloadPlotlyAsPng } from "@/utils/plotlyPngDownload";
import { buildPlotTableCsvContent } from "@/utils/plotTableCsvExport";
import {
  applyHistogram2dStableColorScale,
  getHistogram2dExtentsFromAxisArrays,
  padHistogram2dAxisRange,
} from "../../../utils/histogram2dStableScale";

export default {
  name: "DynamicChart",
  components: {
    ScreenLoader
  },
  mixins: [screenWidthMixin],
  props: {
    loading: { type: Boolean, default: true },
    chartName: { type: String, default: "" },
    chartType: { type: String, required: true },
    xAxisData: { type: Array, default: () => [] },
    yAxisData: { 
      type: Array, 
      default: () => []
    },
    zAxisData: { type: Array, default: () => [] },
    xColorGroupData: { type: Array, default: [] },
    yColorGroupData: { type: Array, default: [] },
    zColorGroupData: { type: Array, default: [] },
    colorGroupKey: { type: String, default: "" },
    colorGroupDisplayName: { type: String, default: "" },
    sampleError: { type: String, default: "" },
    markerSizes: Array,
    xAxisDisplayName: { type: String, default: "" },
    yAxisDisplayNames: { type: Array, default: () => [] },
    zAxisDisplayName: { type: String, default: "" },
    xAxisCaption: { type: Array, default: () => [] },
    yAxisCaption: { type: Array, default: () => [] },
    xBins: { type: Number },
    yBins: { type: Number },
    xAxisScale: { type: String, default: "linear" },
    yAxisScale: { type: String, default: "linear" },
    enablePropagationSort: { type: Boolean, default: true }, // New prop to control propagation sorting
    isCreateEditMode: { type: Boolean, default: false },
    isFilterOpen: { type: Boolean, default: false },
    tableData: { type: Array, default: () => [] },
    xAxisColumn: { type: String, default: "" },
    yAxisColumns: { type: Array, default: () => [] },
    zAxisColumn: { type: String, default: "" },
    xAxisDecimalPrecision: { type: Number, default: 2 },
    yAxisDecimalPrecision: { type: Number, default: 2 },
    zAxisDecimalPrecision: { type: Number, default: 2 },
  },
  data() {
    
    return {
      chartId: "dynamicChart",
      modalChartId: "modalChart",
        chartHeight: this.calculateChartHeight(),
      isModalOpen: false,
      hasValidXColorGroupData: false,
      hasValidYColorGroupData: false,
      previousSidebarState: null,
      cross: require("../../../assets/Svg/Chart/chart-cross.svg"),
      camIcon: require("../../../assets/Svg/camIcon.svg"),
      xAxisScaleModal: this.xAxisScale, // Initialize with prop value
      yAxisScaleModal: this.yAxisScale, // Initialize with prop value
      isModalXLog: this.xAxisScale === "log", // Set initial value based on the prop
      isModalYLog: this.yAxisScale === "log", // Set initial value based on the prop
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    chartWrapperClasses() {
      return {
        'chart-wrapper': true
      };
    }
  },
  watch: {
    xAxisData: {
      handler(newVal, oldVal) {
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    yAxisData: {
      handler(newVal, oldVal) {
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    zAxisData: {
      handler(newVal, oldVal) {
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    xColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    yColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    zColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    chartType: {
      handler(newVal, oldVal) {
        if (this.isCreateEditMode && oldVal && oldVal !== newVal) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    chartName: {
      handler(newVal, oldVal) {
        if (this.isCreateEditMode && oldVal && oldVal.trim() !== '' && (!newVal || newVal.trim() === '')) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        }
      }
    },
    colorGroupKey: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.plotChart();
        }
        if (this.isCreateEditMode && oldVal && oldVal.trim() !== '' && (!newVal || newVal.trim() === '')) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        }
      }
    },
    xBins: {
      handler() {
        this.plotChart();
      }
    },
    yBins: {
      handler() {
        this.plotChart();
      }
    },
    xAxisScale(newValue) {
      this.xAxisScaleModal = newValue;
      this.isModalXLog = newValue === "log";
      this.plotChart();
    },
    yAxisScale(newValue) {
      this.yAxisScaleModal = newValue;
      this.isModalYLog = newValue === "log";
      this.plotChart();
    },

    isCreateEditMode: {
      handler(newVal, oldVal) {
        this.$nextTick(() => {
          this.detectEditToCreateTransition();
        });
      }
    },
    isFilterOpen: {
      handler() {
        this.plotChart();
      }
    },
    isModalOpen: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.plotModalChart();
            }, 150);
          });
        }
      }
    },
    isDarkMode() {
      this.plotChart();
    }
    },
    mounted() {
      this.handleResize = () => {
        this.chartHeight = this.calculateChartHeight();
        this.$nextTick(() => {
          this.plotChart();
        });
      };
      window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    },
    methods: {
      calculateChartHeight() {
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        const zoomLevel = window.devicePixelRatio || 1;
        
        let baseHeight = 500;
        
        if (screenHeight >= 1080) {
          baseHeight = Math.min(screenHeight * 0.5, 700); 
        } else if (screenHeight >= 768) {
          baseHeight = Math.min(screenHeight * 0.45, 550); 
        } else {
          baseHeight = Math.min(screenHeight * 0.4, 450); 
        }
        
        if (zoomLevel > 1) {
          baseHeight = baseHeight * 0.9; 
        }
        
        return Math.max(baseHeight, 300); 
      },
      calculateModalChartHeight() {
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        const zoomLevel = window.devicePixelRatio || 1;
        let baseHeight = 600;
        
        if (screenHeight >= 1080) {
          baseHeight = Math.min(screenHeight * 0.6, 800); 
        } else if (screenHeight >= 768) {
          baseHeight = Math.min(screenHeight * 0.55, 650); 
        } else {
          baseHeight = Math.min(screenHeight * 0.5, 550); 
        }
        
        if (zoomLevel > 1) {
          baseHeight = baseHeight * 0.9; 
        }
        
        return Math.max(baseHeight, 380); 
      },
    clearChartData() {
      if (this.$refs[this.chartId]) {
        try {
          Plotly.purge(this.$refs[this.chartId]);
        } catch (error) {
          console.warn('Error purging chart:', error);
        }
      }
      if (this.$refs[this.modalChartId]) {
        try {
          Plotly.purge(this.$refs[this.modalChartId]);
        } catch (error) {
          console.warn('Error purging modal chart:', error);
        }
      }
      
      this.xAxisScaleModal = this.xAxisScale;
      this.yAxisScaleModal = this.yAxisScale;
      this.isModalXLog = this.xAxisScale === "log";
      this.isModalYLog = this.yAxisScale === "log";
      
      this.hasValidXColorGroupData = false;
      this.hasValidYColorGroupData = false;
    },
    
    forceClearForModeSwitch() {
      this.clearChartData();
      
      this.lastModeState = null;
      
      this.$nextTick(() => {
        this.plotChart();
      });
    },
    
    detectEditToCreateTransition() {
      const isTransitioning = this.isCreateEditMode && 
                             this.chartName && this.chartName.trim() !== '' &&
                             (!this.xAxisData || this.xAxisData.length === 0) &&
                             (!this.yAxisData || this.yAxisData.length === 0);
      
      if (isTransitioning) {
        this.forceClearForModeSwitch();
      }
    },

    getUOMForProperty(columnName) {
      if (this.tableData && this.tableData.length > 0) {
        const firstRow = this.tableData[0];
        const uomKey = `${columnName}_UOM`;
        if (firstRow && firstRow[uomKey] && firstRow[uomKey] !== 'data-not-available') {
          return firstRow[uomKey];
        }
      }
      return null;
    },

    formatAxisTitle(displayName, uom) {
      if (uom && uom.trim() !== '') {
        return `${displayName} (${uom})`;
      }
      return displayName;
    },

    shouldFormatAxisData(axisType) {
      if (!this.tableData || this.tableData.length === 0) {
        return false;
      }

      let columnName;
      if (axisType === 'x') {
        columnName = this.xAxisColumn;
      } else if (axisType === 'y') {
        columnName = this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null;
      } else if (axisType === 'z') {
        columnName = this.zAxisColumn;
      }

      if (!columnName) {
        return false;
      }

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      const shouldFormat = shouldFormatNumbers(values);
      return shouldFormat;
    },

    getFormattedTickFormat(axisType) {
      let decimalPrecision = 2; // Default precision
      
      if (axisType === 'x' && this.xAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.xAxisDecimalPrecision;
      } else if (axisType === 'y' && this.yAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.yAxisDecimalPrecision;
      } else if (axisType === 'z' && this.zAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.zAxisDecimalPrecision;
      }


      if (this.chartType === "2D Histogram") {
        decimalPrecision = 0;
      }

      if (this.shouldFormatAxisData(axisType)) {
        decimalPrecision = 0;
      }

      if (this.shouldFormatAxisData(axisType)) {
        return createCustomTickFormatter(decimalPrecision);
      }
      
      return `.${decimalPrecision}~f`;
    },
    handleModalXScaleToggleChange() {
      this.xAxisScaleModal = this.isModalXLog ? "log" : "linear";
      this.plotModalChart();
    },
    handleModalYScaleToggleChange() {
      this.yAxisScaleModal = this.isModalYLog ? "log" : "linear";
      this.plotModalChart();
    },
    downloadCSV() {
      if (this.tableData && this.tableData.length > 0) {
        const csvContent = buildPlotTableCsvContent(this.tableData);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${this.chartName || "chart"}.csv`);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const rows = [];

      const headers = [this.xAxisDisplayName, ...this.yAxisDisplayNames];
      if (this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
        headers.push(this.zAxisDisplayName);
      }
      rows.push(headers.join(","));

      const maxLength = Math.max(
        this.xAxisData.length, 
        this.yAxisData.reduce((max, arr) => Math.max(max, arr.length), 0),
        this.zAxisData ? this.zAxisData.length : 0
      );

      for (let i = 0; i < maxLength; i++) {
        const x = this.xAxisData[i] !== undefined ? this.xAxisData[i] : "";

        const yValues = this.yAxisData.map(yDataArray => {
          return yDataArray[i] !== undefined ? yDataArray[i] : "";
        });

        const z = (this.zAxisData && this.zAxisData[i] !== undefined) ? this.zAxisData[i] : "";

        const rowData = [x, ...yValues];
        if (this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
          rowData.push(z);
        }
        rows.push(rowData.join(","));
      }

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${this.chartName || "chart"}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadPNG() {
      if (!this.$refs[this.chartId]) {
        return;
      }
      const traces = applyHistogram2dStableColorScale(this.generateTraces(), this.xBins, this.yBins);
      downloadPlotlyAsPng(traces, this.generateLayout(), this.chartName, {
        isDarkMode: this.isDarkMode,
        title: this.chartName,
      });
    },
    plotChart() {
      
      this.$nextTick(() => {
        if (!this.$refs[this.chartId]) {
          return;
        }

        this.clearChartData();
        
        const hasXData = this.xAxisData && this.xAxisData.length > 0;
        const hasYData = this.yAxisData && this.yAxisData.length > 0;
        const isHistogram = this.chartType === "Histogram";
        
        const hasValidData = hasXData && (isHistogram || hasYData);

        if (!hasValidData) {
          const layout = this.generateLayout();
          const config = this.generateConfig();
          
          if (this.chartType === "3D Scatter") {
            if (layout.scene && layout.scene.xaxis) {
              layout.scene.xaxis.title = { text: this.xAxisDisplayName || "X Axis" };
            }
            if (layout.scene && layout.scene.yaxis) {
              layout.scene.yaxis.title = { text: (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ") : "Y Axis") };
            }
            if (layout.scene && layout.scene.zaxis) {
              layout.scene.zaxis.title = { text: this.zAxisDisplayName || "Z Axis" };
            }
          } else {
            if (layout.xaxis) {
              layout.xaxis.title = { text: this.xAxisDisplayName || "" };
            }
            if (layout.yaxis) {
              layout.yaxis.title = { text: (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ") : "") };
            }
          }
          layout.showlegend = false;
          
          Plotly.newPlot(this.$refs[this.chartId], [], layout, config).then(() => {
            this.applyStudioGridTheme();
          });
          return;
        }

        const traces = applyHistogram2dStableColorScale(this.generateTraces(), this.xBins, this.yBins);
        const layout = this.generateLayout();
        const config = this.generateConfig();

        if (!traces || traces.length === 0) {
          const layout = this.generateLayout();
          const config = this.generateConfig();
          
          layout.xaxis.title = { text: "" };
          layout.yaxis.title = { text: "" };
          layout.showlegend = false;
          
          Plotly.newPlot(this.$refs[this.chartId], [], layout, config).then(() => {
            this.applyStudioGridTheme();
          });
          return;
        }

        Plotly.newPlot(this.$refs[this.chartId], traces, layout, config)
          .then(() => {
            Plotly.Plots.resize(this.$refs[this.chartId]);
            this.applyStudioGridTheme();
            this.setupsidebarStateCheck();
          })
          .catch((error) => {
            console.error('❌ DynamicChart: Error creating plot:', error);
          });
      });
    },
    plotModalChart() {
      if (!this.$refs[this.modalChartId]) {
        return;
      }


      const isHistogram = this.chartType === "Histogram";
      if (!isHistogram && (!this.yAxisData || this.yAxisData.length === 0)) {
        try {
          Plotly.purge(this.$refs[this.modalChartId]);
        } catch (error) {
          console.warn('Error purging modal chart:', error);
        }
        return;
      }

      const traces = applyHistogram2dStableColorScale(this.generateTraces(), this.xBins, this.yBins);
      const config = this.generateModalConfig();
      
      const layout = this.generateModalLayout();

      this.$nextTick(() => {
        try {
          Plotly.purge(this.$refs[this.modalChartId]);
          

          Plotly.newPlot(this.$refs[this.modalChartId], traces, layout, config)
            .then(() => {
              Plotly.Plots.resize(this.$refs[this.modalChartId]);
              applyPlotlyGridLineStroke(this.$refs[this.modalChartId], this.isDarkMode);
            })
            .catch((error) => {
              console.error('Error creating modal plot:', error);
            });
        } catch (error) {
          console.error('Error plotting modal chart:', error);
        }
      });
    },

    generateTraces() {
      const traceType = this.getTraceType();
      const traceMode = this.getTraceMode();
      const scatterMarkerSizes = 8;
      const scatter3DMarkerSizes = 6; // 3D scatter plot marker size

      const hasXData = this.xAxisData && this.xAxisData.length > 0;
      const hasYData = this.yAxisData && this.yAxisData.length > 0;
      const isHistogram = this.chartType === "Histogram";
      
      if (!hasXData) {
        return [];
      }

      if (!isHistogram && !hasYData) {
        return [];
      }

      if (this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0 && this.xAxisColumn && this.yAxisColumns) {
        
        const hasColorGroup = this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasColorGroup) {
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            const legend = row[this.colorGroupKey];
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = {
                data: [],
                legend: legend
              };
            }
            
            const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                        (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
            
            let yVal = null;
            let matchingKey = null;
            if (this.yAxisColumns && this.yAxisColumns.length > 0) {
              matchingKey = this.yAxisColumns[0];
              yVal = row[matchingKey];
            } else if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
              const yDisplayName = this.yAxisDisplayNames[0];
              const availableKeys = Object.keys(row);
              
              const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID'];
              const dataColumns = availableKeys.filter(key => 
                !systemColumns.includes(key) && 
                !key.endsWith('_UOM') && 
                !key.endsWith('_decimal') &&
                !key.endsWith('_uom') &&  // lowercase version
                !key.endsWith('_DECIMAL') // uppercase version
              );
              
              let matchingKey = dataColumns.find(key => {
                const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                return normalizedKey === normalizedDisplay || 
                       normalizedKey.includes(normalizedDisplay) || 
                       normalizedDisplay.includes(normalizedKey);
              });
              
              if (!matchingKey && dataColumns.length === 0) {
                matchingKey = availableKeys.find(key => {
                  const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                  const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                  return normalizedKey === normalizedDisplay || 
                         normalizedKey.includes(normalizedDisplay) || 
                         normalizedDisplay.includes(normalizedKey);
                });
              }
              
              if (matchingKey) {
                yVal = row[matchingKey];
              } else {
                const availableKeys = Object.keys(row);
                const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID', 'uniqueID'];
                const dataColumns = availableKeys.filter(key => 
                  !systemColumns.includes(key) && 
                  !key.endsWith('_UOM') && 
                  !key.endsWith('_decimal') &&
                  !key.endsWith('_uom') &&  
                  !key.endsWith('_DECIMAL') &&
                  row[key] !== null && 
                  row[key] !== undefined && 
                  row[key] !== ''
                );
                
                for (const colName of dataColumns) {
                  const value = row[colName];
                  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
                    yVal = value;
                    matchingKey = colName;
                    break;
                  }
                }
              }
            }
            
            const zVal = row[this.zAxisColumn] || row[this.zAxisDisplayName] || null;
          
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              idGroups[dataId].data.push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
            }
          });
          
          const traces = [];
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          const legendColorMap = {};
          let colorIndex = 0;
          const shownLegends = new Set();
          
          Object.keys(idGroups).forEach((idKey) => {
            const idGroup = idGroups[idKey];
            const validData = idGroup.data;
            const legend = idGroup.legend;
            
            if (validData.length === 0) {
              return;
            }

            if (!legendColorMap[legend]) {
              legendColorMap[legend] = colors[Object.keys(legendColorMap).length % colors.length];
            }
            const traceColor = legendColorMap[legend];

            const isFirstOfType = !shownLegends.has(legend);
            if (isFirstOfType) {
              shownLegends.add(legend);
            }

            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              return 0;
            });
            const validXData = sortedData.map(item => item.x);
            const validYData = sortedData.map(item => item.y);
            const validZData = sortedData.map(item => item.z);

            const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

            const trace = {
              x: validXData,
              y: processedYData,
              z: validZData,
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: traceColor // Use the same color for all traces of the same legend type
              },
              name: this.truncateLegendLabel(legend), // Use legend type as name
              legendgroup: legend, // Group by legend type
              showlegend: isFirstOfType, // Show only one legend entry per type
              connectgaps: false,
              customdata: validXData.map((xValue, dataIndex) => {
                const item = sortedData[dataIndex];
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
            shownLegends.add(legend);
            colorIndex++;
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = [];
            }
            
            const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                        (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
            
            let yVal = null;
            let matchingKey = null;
            if (this.yAxisColumns && this.yAxisColumns.length > 0) {
              matchingKey = this.yAxisColumns[0];
              yVal = row[matchingKey];
            } else if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
              const yDisplayName = this.yAxisDisplayNames[0];
              const availableKeys = Object.keys(row);
              
              const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID'];
              const dataColumns = availableKeys.filter(key => 
                !systemColumns.includes(key) && 
                !key.endsWith('_UOM') && 
                !key.endsWith('_decimal') &&
                !key.endsWith('_uom') &&  // lowercase version
                !key.endsWith('_DECIMAL') // uppercase version
              );
              
              let matchingKey = dataColumns.find(key => {
                const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                return normalizedKey === normalizedDisplay || 
                       normalizedKey.includes(normalizedDisplay) || 
                       normalizedDisplay.includes(normalizedKey);
              });
              
              if (!matchingKey && dataColumns.length === 0) {
                matchingKey = availableKeys.find(key => {
                  const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                  const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                  return normalizedKey === normalizedDisplay || 
                         normalizedKey.includes(normalizedDisplay) || 
                         normalizedDisplay.includes(normalizedKey);
                });
              }
              
              if (matchingKey) {
                yVal = row[matchingKey];
              } else {
                const availableKeys = Object.keys(row);
                const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID', 'uniqueID'];
                const dataColumns = availableKeys.filter(key => 
                  !systemColumns.includes(key) && 
                  !key.endsWith('_UOM') && 
                  !key.endsWith('_decimal') &&
                  !key.endsWith('_uom') &&  
                  !key.endsWith('_DECIMAL') &&
                  row[key] !== null && 
                  row[key] !== undefined && 
                  row[key] !== ''
                );
                
                for (const colName of dataColumns) {
                  const value = row[colName];
                  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
                    yVal = value;
                    matchingKey = colName;
                    break;
                  }
                }
              }
            }
            
            const zVal = row[this.zAxisColumn] || row[this.zAxisDisplayName] || null;
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              idGroups[dataId].push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
            }
          });
          
          const traces = [];
          const singleColor = "rgba(0, 100, 250, 0.8)"; // Same blue color for all
          
          Object.keys(idGroups).forEach((idKey) => {
            const validData = idGroups[idKey];
            
            if (validData.length === 0) {
              return;
            }

            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              return 0;
            });
            const validXData = sortedData.map(item => item.x);
            const validYData = sortedData.map(item => item.y);
            const validZData = sortedData.map(item => item.z);

            const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

            const trace = {
              x: validXData,
              y: processedYData,
              z: validZData,
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: singleColor
              },
              name: `Data Series ${Object.keys(idGroups).indexOf(idKey) + 1}`,
              showlegend: false,
              connectgaps: false,
              customdata: validXData.map((xValue, dataIndex) => {
                const item = sortedData[dataIndex];
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        }
      }

      if (this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0 && (!this.xAxisColumn || !this.yAxisColumns)) {
        const traces = [];
        
        const hasColorGroup = this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasColorGroup) {
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            const legend = row[this.colorGroupKey];
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = {
                data: [],
                legend: legend
              };
            }
            
            const xVal = this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : 
                        (this.xAxisData && this.xAxisData[index] !== undefined ? this.xAxisData[index] : null);
            
            let yVal = null;
            if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined) {
              yVal = row[this.yAxisDisplayNames[0]];
            } else if (this.yAxisData && this.yAxisData[0] && this.yAxisData[0][index] !== undefined) {
              yVal = this.yAxisData[0][index];
            }
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
             
              
              idGroups[dataId].data.push({ x: processedXVal, y: yVal, originalRow: row });
            }
          });
          
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          const legendColorMap = {};
          const shownLegends = new Set();
          
          Object.keys(idGroups).forEach((idKey) => {
            const idGroup = idGroups[idKey];
            const validData = idGroup.data;
            const legend = idGroup.legend;
            
            if (validData.length === 0) {
              return;
            }

            if (!legendColorMap[legend]) {
              legendColorMap[legend] = colors[Object.keys(legendColorMap).length % colors.length];
            }
            const traceColor = legendColorMap[legend];

            const isFirstOfType = !shownLegends.has(legend);
            if (isFirstOfType) {
              shownLegends.add(legend);
            }

            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              return 0;
            });

            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: traceColor
              },
              name: isFirstOfType ? this.truncateLegendLabel(legend) : undefined,
              showlegend: isFirstOfType,
              connectgaps: false,
              line: {
                color: traceColor,
                width: 2
              },
              customdata: sortedData.map(item => {
                return item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}<br>
%{y}<br>
%{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = [];
            }
            
            const xVal = this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : 
                        (this.xAxisData && this.xAxisData[index] !== undefined ? this.xAxisData[index] : null);
            
            let yVal = null;
            if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined) {
              yVal = row[this.yAxisDisplayNames[0]];
            } else if (this.yAxisData && this.yAxisData[0] && this.yAxisData[0][index] !== undefined) {
              yVal = this.yAxisData[0][index];
            }
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              
              idGroups[dataId].push({ x: processedXVal, y: yVal, originalRow: row });
            }
          });
          
          Object.keys(idGroups).forEach((idKey) => {
            const validData = idGroups[idKey];
            
            if (validData.length === 0) {
              return;
            }

            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              return 0;
            });

            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: "rgba(0, 100, 250, 0.8)"
              },
              showlegend: false,
              connectgaps: false,
              line: {
                color: "rgba(0, 100, 250, 0.8)",
                width: 2
              },
              customdata: sortedData.map(item => {
                return item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}<br>
%{y}<br>
%{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        }
      }

      if (this.chartType === "Line Plot" && this.xAxisData && this.yAxisData && this.xAxisData.length > 0 && this.yAxisData.length > 0 && !this.tableData) {
        const traces = [];
        
        const hasValidColorGroup = this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasValidColorGroup) {
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          const groupedData = {};
          this.xAxisData.forEach((xVal, index) => {
            const yVal = this.yAxisData[0] && this.yAxisData[0][index] !== undefined ? this.yAxisData[0][index] : null;
            const colorGroupValue = this.xColorGroupData[Object.keys(this.xColorGroupData)[0]] && 
                                   this.xColorGroupData[Object.keys(this.xColorGroupData)[0]][index] ? 
                                   this.xColorGroupData[Object.keys(this.xColorGroupData)[0]][index] : 'Default';
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              if (!groupedData[colorGroupValue]) {
                groupedData[colorGroupValue] = [];
              }
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              groupedData[colorGroupValue].push({ x: processedXVal, y: yVal });
            }
          });
          
          Object.keys(groupedData).forEach((groupKey, index) => {
            const groupData = groupedData[groupKey];
            if (groupData.length === 0) return;
            
            const sortedData = groupData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              if (typeof a.x === 'string' && typeof b.x === 'string') {
                return a.x.localeCompare(b.x);
              }
              return String(a.x).localeCompare(String(b.x));
            });
            
            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: colors[index % colors.length]
              },
              name: this.truncateLegendLabel(groupKey),
              showlegend: true,
              connectgaps: false,
              customdata: sortedData.map((item, dataIndex) => {
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };
            
            traces.push(trace);
          });
          
          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          const validData = [];
          this.xAxisData.forEach((xVal, index) => {
            const yVal = this.yAxisData[0] && this.yAxisData[0][index] !== undefined ? this.yAxisData[0][index] : null;
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              validData.push({ x: processedXVal, y: yVal });
            }
          });
          
          if (validData.length === 0) {
            return [];
          }
          
          const sortedData = validData.sort((a, b) => {
            if (typeof a.x === 'number' && typeof b.x === 'number') {
              return a.x - b.x;
            }
            return 0;
          });
          
          const trace = {
            x: sortedData.map(item => item.x),
            y: sortedData.map(item => item.y),
            type: 'scatter',
            mode: 'lines',
            marker: {
              size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
              color: 'rgba(0, 100, 250, 0.8)'
            },
            name: this.yAxisDisplayNames[0] || 'Y-Axis',
            showlegend: false,
            connectgaps: false,
            customdata: sortedData.map((item, dataIndex) => {
              return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
            }),
            hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
          };
          
          return [trace];
        }
      }

      this.hasValidXColorGroupData = Object.values(this.xColorGroupData).some(group => Array.isArray(group) && group.length > 0);
      this.hasValidYColorGroupData = Object.values(this.yColorGroupData).some(group => Array.isArray(group) && group.length > 0);

      const hasColorGroupSelected = this.colorGroupKey && this.colorGroupKey.trim() !== '';
      
      const hasValidColorGroupForHistogram = this.chartType === "Histogram" 
        ? this.hasValidXColorGroupData 
        : (this.hasValidXColorGroupData && this.hasValidYColorGroupData);

      if (this.chartType === "Histogram") {
        const colors = [
          "rgba(0, 100, 250, 0.6)",   // Blue
          "rgba(255, 99, 71, 0.6)",   // Red
          "rgba(50, 205, 50, 0.6)",   // Green
          "rgba(255, 215, 0, 0.6)",   // Gold
          "rgba(147, 112, 219, 0.6)", // Purple
          "rgba(255, 165, 0, 0.6)"    // Orange
        ];
        const borderColor = "rgba(0, 0, 0, 1)";
        const borderWidth = 1;

        if (hasValidColorGroupForHistogram && hasColorGroupSelected) {
          const sortedLegendKeys = Object.keys(this.xColorGroupData).sort();
          
          let unifiedXbinsConfig = undefined;
          let isNumericData = false;
          
          const allXData = [];
          for (const key of sortedLegendKeys) {
            const xData = this.xColorGroupData[key];
            const validXData = xData.filter(value => 
              value !== null && 
              value !== undefined && 
              value !== ''
            );
            allXData.push(...validXData);
          }
          
          if (allXData.length > 0) {
            isNumericData = allXData.every(value => {
              if (typeof value === 'number') {
                return !isNaN(value);
              }
              if (typeof value === 'string') {
                const num = parseFloat(value);
                return !isNaN(num) && isFinite(num);
              }
              return false;
            });
            
            if (isNumericData) {
              const numericData = allXData.map(value => {
                if (typeof value === 'string') {
                  return parseFloat(value);
                }
                return value;
              });
              
              const minVal = Math.min(...numericData);
              const maxVal = Math.max(...numericData);
              const range = maxVal - minVal;
              
            if (range > 0) {
              const adjustedMinVal = Math.max(0, minVal);
              const adjustedRange = maxVal - adjustedMinVal;
              
              const padding = adjustedRange * 0.001; // 0.1% padding
              const binSize = (adjustedRange + 2 * padding) / this.xBins;
              
              unifiedXbinsConfig = {
                start: Math.max(0, adjustedMinVal - padding), // Ensure start is never negative
                end: maxVal + padding,
                size: binSize
              };
            }
            }
          }
          
          return sortedLegendKeys.map((key, index) => {
            const xData = this.xColorGroupData[key];
            const validXData = xData.filter(value => 
              value !== null && 
              value !== undefined && 
              value !== ''
            );
            
            if (validXData.length === 0) {
              return null;
            }
            
            let sortedData;
            
            if (isNumericData) {
              sortedData = validXData.map(value => {
                if (typeof value === 'string') {
                  return parseFloat(value);
                }
                return value;
              }).sort((a, b) => a - b);
            } else {
              sortedData = validXData;
            }
            
            return {
              x: sortedData,
              type: traceType,
              autobinx: false,
              mode: traceMode,
              name: this.truncateLegendLabel(key),
              nbinsx: this.xBins, // Use exact number of bins
              xbins: unifiedXbinsConfig, // Use unified bin boundaries for all groups
              marker: {
                color: colors[index % colors.length],
                line: {
                  color: borderColor,
                  width: borderWidth
                }
              },
              customdata: sortedData.map((value, dataIndex) => {
                return key; // Return the legend group name (e.g., "FD", "ML", "AI", "AGI")
              }),
              hovertemplate: `
  <b>${key}</b><br>
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
            };
          }).filter(trace => trace !== null);
        }
        else {
          const validXData = this.xAxisData.filter(value => 
            value !== null && 
            value !== undefined && 
            value !== ''
          );
          
          if (validXData.length === 0) {
            return [];
          }
          
          const isNumericData = validXData.every(value => {
            if (typeof value === 'number') {
              return !isNaN(value);
            }
            if (typeof value === 'string') {
              const num = parseFloat(value);
              return !isNaN(num) && isFinite(num);
            }
            return false;
          });
          
          let sortedData;
          
          if (isNumericData) {
            sortedData = validXData.map(value => {
              if (typeof value === 'string') {
                return parseFloat(value);
              }
              return value;
            }).sort((a, b) => a - b);
          } else {
            sortedData = validXData;
          }
          
          let xbinsConfig = undefined;
          if (isNumericData && sortedData.length > 0) {
            const minVal = Math.min(...sortedData);
            const maxVal = Math.max(...sortedData);
            const range = maxVal - minVal;
            
            if (range > 0) {
              const adjustedMinVal = Math.max(0, minVal);
              const adjustedRange = maxVal - adjustedMinVal;
              
              const padding = adjustedRange * 0.001; // 0.1% padding
              const binSize = (adjustedRange + 2 * padding) / this.xBins;
              
              xbinsConfig = {
                start: Math.max(0, adjustedMinVal - padding), // Ensure start is never negative
                end: maxVal + padding,
                size: binSize
              };
            }
          } else if (!isNumericData) {
            xbinsConfig = undefined;
          }
          
          return [{
            x: sortedData,
            type: traceType,
            name: this.chartName || "Histogram",
            autobinx: false,
            nbinsx: this.xBins, // Use exact number of bins
            xbins: xbinsConfig, // Use calculated bin boundaries
            marker: {
              color: colors[0],
              line: {
                color: borderColor,
                width: borderWidth
              }
            },
            hovertemplate: `
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
          }];
        }
      } else if (hasValidColorGroupForHistogram && hasColorGroupSelected) {
        if (this.chartType === "Line Plot") {
        const colors = [
          "rgba(0, 100, 250, 0.6)",   // Blue
          "rgba(255, 99, 71, 0.6)",   // Red
          "rgba(50, 205, 50, 0.6)",   // Green
          "rgba(255, 215, 0, 0.6)",   // Gold
          "rgba(147, 112, 219, 0.6)", // Purple
          "rgba(255, 165, 0, 0.6)"    // Orange
        ];
        
        const idGroups = {};
        
        this.tableData.forEach((row) => {
          const dataId = row.uniqueID;
          const legend = row[this.colorGroupKey];
          
          if (!idGroups[dataId]) {
            idGroups[dataId] = {
              data: [],
              legend: legend
            };
          }
          
          const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                      (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
          const yVal = this.yAxisColumns && this.yAxisColumns.length > 0 && row[this.yAxisColumns[0]] !== undefined ? row[this.yAxisColumns[0]] : 
                      (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined ? row[this.yAxisDisplayNames[0]] : null);
          const zVal = row[this.zAxisDisplayName] || null;
          
          if (xVal !== null && xVal !== undefined && xVal !== '' && 
              yVal !== null && yVal !== undefined && yVal !== '') {
            
            let processedXVal = xVal;
            if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
              processedXVal = parseFloat(xVal);
            }
            
            idGroups[dataId].data.push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
          }
        });
        
      
        
        const traces = [];
        let colorIndex = 0;
        const shownLegends = new Set(); // Track which legends we've already shown
        
        Object.keys(idGroups).forEach((idKey, index) => {
          const idGroup = idGroups[idKey];
          const validData = idGroup.data;
          const legend = idGroup.legend;
          const markerSize = (this.chartType === "Scatter Plot") ? scatterMarkerSizes : 
                             (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes;
          
          if (validData.length === 0) {
            return;
          }

          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
          let sortedData;
          
          if (isXNumeric) {
            if (this.enablePropagationSort) {
              sortedData = validData.sort((a, b) => {
                if (typeof a.x === 'number' && typeof b.x === 'number') {
                  return a.x - b.x;
                }
                return 0;
              });
              
              if (this.chartType === "Line Plot" || this.chartType === "Scatter Plot") {
                const firstY = sortedData[0]?.y;
                const lastY = sortedData[sortedData.length - 1]?.y;
                
                if (firstY !== undefined && lastY !== undefined && firstY > lastY) {
                  void 0;
                }
              }
            } else {
              sortedData = validData;
            }
          } else {
            sortedData = validData;
          }
          
          const validXData = sortedData.map(item => item.x);
          const validYData = sortedData.map(item => item.y);
          const validZData = sortedData.map(item => item.z);

          const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

          const trace = {
            x: validXData,
            y: processedYData,
            z: validZData,
            type: traceType,
            mode: traceMode,
            ...(this.chartType !== 'Line Plot' || (this.chartType === 'Line Plot' && hasValidColorGroupForHistogram && hasColorGroupSelected) ? {
            marker: {
              size: markerSize,
              color: colors[colorIndex % colors.length], // Use consistent color assignment
              ...(this.chartType === 'Histogram' ? {
                line: {
                  color: 'rgba(0, 0, 0, 1)',     // Solid black border
                  width: 1                       // Border width
                }
              } : {})
              }
            } : {}),
            name: this.truncateLegendLabel(legend), // Use the legend from the _id group
            legendgroup: legend, // Group by legend for proper legend behavior
            showlegend: !shownLegends.has(legend), // Show legend only for the first trace of each legend group
            connectgaps: false,

            customdata: validXData.map((xValue, dataIndex) => {
              const item = sortedData[dataIndex];
              return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
            }),

            hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
          };

          if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
            const maxMarkerSize = Math.max(...this.markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

          if (this.chartType === "2D Histogram") {
            trace.colorscale = [
              [0.0, 'rgba(247,251,255,255)'],
              [0.25, 'rgba(193,217,237,255)'],
              [0.5, 'rgba(97,167,210,255)'],
              [0.75, 'rgba(24,101,172,255)'],
              [1.0, 'rgba(8,48,108,255)']
            ]
            trace.colorbar = {
              title: {
                text: 'Number of Samples', // Title for the color bar
                side: 'right',
              }
            };
          }

          if (["2D Histogram", "Bubble", "Heatmap"].includes(this.chartType)) {
            trace.autobinx = false;
            trace.autobiny = false;
            
            const isXNumeric = validXData.every(val => typeof val === 'number' && !isNaN(val));
            const isYNumeric = processedYData.every(val => typeof val === 'number' && !isNaN(val));
            
            if (isXNumeric) {
              trace.xbins = { 
                size: this.calculateBinSize(validXData, this.xBins),
                start: Math.min(...validXData),
                end: Math.max(...validXData)
              };
            }
            
            if (isYNumeric) {
              trace.ybins = { 
                size: this.calculateBinSize(processedYData, this.yBins),
                start: Math.min(...processedYData),
                end: Math.max(...processedYData)
              };
            }
          }

          traces.push(trace);
          
          shownLegends.add(legend);
          colorIndex++;
        });

      return traces.filter(trace => trace !== null);
        } else {
          return Object.keys(this.xColorGroupData).map((key) => {
            const xData = this.xColorGroupData[key];
            let yData = this.yColorGroupData[key];
            const zData = this.zColorGroupData[key];
            const markerSizes = [...zData];

            if (!Array.isArray(xData) || !Array.isArray(yData) || xData.length !== yData.length) {
              return null;
            }

            const validData = xData.map((xVal, i) => {
              const yVal = yData[i];
              if (xVal !== null && xVal !== undefined && xVal !== '' && 
                  yVal !== null && yVal !== undefined && yVal !== '') {
                return { x: xVal, y: yVal, index: i };
              }
              return null;
            }).filter(item => item !== null);

            if (validData.length === 0) {
              return null;
            }

            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              return 0;
            });

            yData = sortedData.map(item => Array.isArray(item.y) ? item.y[0] : item.y);

            const markerSize = (this.chartType === "Scatter Plot")
              ? (scatterMarkerSizes)
              : (this.chartType === "3D Scatter")
              ? (scatter3DMarkerSizes)
              : (markerSizes);

            const trace = {
              x: sortedData.map(item => item.x),
              y: yData,
              z: zData,
              type: traceType,
              mode: traceMode,
              name: this.truncateLegendLabel(key),
              marker: {
                size: markerSize
              },
              connectgaps: false,
            };

            if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
              const maxMarkerSize = Math.max(...markerSizes);
              trace.marker = {
                ...trace.marker,
                sizemode: 'area',
                sizeref: 2.0 * maxMarkerSize / (80 * 2),
                sizemin: 2,
              };
            }

            if (["2D Histogram"].includes(this.chartType)) {
              trace.autobinx = false;
              trace.autobiny = false;
              trace.xbins = { 
                size: this.calculateBinSize(sortedData.map(item => item.x), this.xBins),
                start: Math.min(...sortedData.map(item => item.x)),
                end: Math.max(...sortedData.map(item => item.x))
              };
              trace.ybins = { 
                size: this.calculateBinSize(yData, this.yBins),
                start: Math.min(...yData),
                end: Math.max(...yData)
              };
            }

            return trace;
          }).filter(trace => trace !== null);
        }
    }
    else if (this.tableData && this.tableData.length > 0) {
      if (this.chartType === "Line Plot") {
      const idGroups = {};
      
      this.tableData.forEach((row) => {
        const dataId = row.uniqueID;
        
        if (!idGroups[dataId]) {
          idGroups[dataId] = [];
        }
        
        const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                    (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
        const yVal = this.yAxisColumns && this.yAxisColumns.length > 0 && row[this.yAxisColumns[0]] !== undefined ? row[this.yAxisColumns[0]] : 
                    (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined ? row[this.yAxisDisplayNames[0]] : null);
        const zVal = row[this.zAxisDisplayName] || null;
        
        if (xVal !== null && xVal !== undefined && xVal !== '' && 
            yVal !== null && yVal !== undefined && yVal !== '') {
          
          let processedXVal = xVal;
          if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
            processedXVal = parseFloat(xVal);
          }
          
          idGroups[dataId].push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
        }
      });
      
      const traces = [];
      const singleColor = "rgba(0, 100, 250, 0.8)"; // Same blue color for all
      
      Object.keys(idGroups).forEach((idKey) => {
        const validData = idGroups[idKey];
        
        if (validData.length === 0) {
          return;
        }

        const sortedData = validData.sort((a, b) => {
          if (typeof a.x === 'number' && typeof b.x === 'number') {
            return a.x - b.x;
          }
          return 0;
        });
        const validXData = sortedData.map(item => item.x);
        const validYData = sortedData.map(item => item.y);
        const validZData = sortedData.map(item => item.z);

        const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

        let traceType = 'scatter';
        let traceMode = 'markers';
        
        if (this.chartType === "Line Plot") {
          traceMode = 'lines+markers';
        } else if (this.chartType === "Scatter Plot") {
          traceMode = 'markers';
        }

        const trace = {
          x: validXData,
          y: processedYData,
          z: validZData,
          type: traceType,
          mode: traceMode,
          marker: {
            size: (this.chartType === "Scatter Plot") ? scatterMarkerSizes : 
                  (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
            color: singleColor
          },
          name: `Data Series ${Object.keys(idGroups).indexOf(idKey) + 1}`, // Generic name
          showlegend: false, // No legend since it's not really a legend
          connectgaps: false,
          customdata: validXData.map((xValue, dataIndex) => {
            const item = sortedData[dataIndex];
            return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
          }),
          hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
        };

        traces.push(trace);
      });

      return traces.filter(trace => trace !== null);
      } else {
        return this.yAxisData.map((yData, index) => {
          const xData = this.xAxisData;
          const zData = this.zAxisData;

          const isInvalidData = (data) => !data || data.every(value => value === null || value === undefined);

          if (isInvalidData(xData) || isInvalidData(yData)) {
            return null; // Skip plotting for invalid data
          }

          const validData = [];
          const minLength = Math.min(xData.length, yData.length);
          
          for (let i = 0; i < minLength; i++) {
            const xVal = xData[i];
            const yVal = yData[i];
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              validData.push({ x: processedXVal, y: yVal, index: i });
            }
          }

          if (validData.length === 0) {
            return null;
          }

          let sortedData;
          if (this.enablePropagationSort) {
          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
            
            if (isXNumeric) {
              sortedData = validData.sort((a, b) => {
                if (typeof a.x === 'number' && typeof b.x === 'number') {
                  return a.x - b.x;
                }
                return 0;
              });
              
              if (this.chartType === "Line Plot" || this.chartType === "Scatter Plot") {
                const firstY = sortedData[0]?.y;
                const lastY = sortedData[sortedData.length - 1]?.y;
                
                if (firstY !== undefined && lastY !== undefined && firstY > lastY) {
                  void 0;
                }
              }
            } else {
              sortedData = validData;
            }
          } else {
            sortedData = validData;
          }
          
          const validXData = sortedData.map(item => item.x);
          const validYData = sortedData.map(item => item.y);

          const markerSize = (this.chartType === "Scatter Plot")
            ? (scatterMarkerSizes)
            : (this.chartType === "3D Scatter")
            ? (scatter3DMarkerSizes)
            : (this.markerSizes);

          const trace = {
            x: validXData,
            y: validYData,
            z: zData,
            type: traceType,
            mode: traceMode,
            connectgaps: false,
            marker: {
              size: markerSize,
              ...(this.chartType !== 'Histogram' && !(hasValidColorGroupForHistogram && hasColorGroupSelected) ? {
                color: 'rgba(0, 100, 250, 0.8)' // Blue color for all plots without legend (except histogram)
              } : {}),
              ...(this.chartType === 'Histogram' ? {
                color: 'rgba(0, 100, 250, 0.6)', // Blue with some transparency
                line: {
                  color: 'rgba(0, 0, 0, 1)',     // Solid black border
                  width: 1                       // Border width
                }
              } : {})
            },
            name: this.yAxisDisplayNames[index] || `Y-Axis ${index + 1}`,
            customdata: validXData && validXData.length > 0
              ? validXData.map((xValue, dataIndex) => {
                const originalIndex = sortedData[dataIndex].index;
                if (this.xAxisCaption && this.xAxisCaption.length > 0 && originalIndex < this.xAxisCaption.length) {
                  return this.xAxisCaption[originalIndex];
                }
                return 'N/A';
              })
              : validYData.map((yValue, i) => {
                const originalIndex = sortedData[i].index;
                if (this.yAxisCaption && this.yAxisCaption.length > 0 && originalIndex < this.yAxisCaption.length) {
                  return this.yAxisCaption[originalIndex];
                }
                return 'N/A';
              }),

            hovertemplate: `
  %{x}  %{y}  %{customdata}
  <extra></extra>
`,
          };
          if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
            const maxMarkerSize = Math.max(...this.markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

          if (this.chartType === "2D Histogram") {
            trace.colorscale = [
              [0.0, 'rgba(247,251,255,255)'],
              [0.25, 'rgba(193,217,237,255)'],
              [0.5, 'rgba(97,167,210,255)'],
              [0.75, 'rgba(24,101,172,255)'],
              [1.0, 'rgba(8,48,108,255)']
            ];
            trace.colorbar = {
              title: {
                text: 'Number of Samples', // Title for the color bar
                side: 'right',
              }
            };
          }

          if (["2D Histogram", "Bubble", "Heatmap"].includes(this.chartType)) {
            trace.autobinx = false;
            trace.autobiny = false;
            
            const isXNumeric = validXData.every(val => typeof val === 'number' && !isNaN(val));
            const isYNumeric = validYData.every(val => typeof val === 'number' && !isNaN(val));
            
            if (isXNumeric) {
              trace.xbins = { 
                size: this.calculateBinSize(validXData, this.xBins),
                start: Math.min(...validXData),
                end: Math.max(...validXData)
              };
            }
            
            if (isYNumeric) {
              trace.ybins = { 
                size: this.calculateBinSize(validYData, this.yBins),
                start: Math.min(...validYData),
                end: Math.max(...validYData)
              };
            }
          }

          return trace;
        }).filter(trace => trace !== null); // Ensure no null traces are included in the final array
      }
    }
    },
    truncateLegendLabel(value, maxLen = 40) {
      const s = value == null ? "" : String(value);
      if (s.length <= maxLen) {
        return s;
      }
      return `${s.slice(0, Math.max(1, maxLen - 1))}\u2026`;
    },
    computeColorGroupLegendMargin() {
      const key = this.colorGroupKey && String(this.colorGroupKey).trim();
      if (!key || !this.tableData || !this.tableData.length) {
        return 62;
      }
      const lengths = [];
      const seen = new Set();
      for (let i = 0; i < this.tableData.length; i++) {
        const row = this.tableData[i];
        if (!row || row[key] === undefined || row[key] === null || row[key] === "") {
          continue;
        }
        const raw = String(row[key]);
        const disp = this.truncateLegendLabel(raw, 40);
        if (seen.has(disp)) {
          continue;
        }
        seen.add(disp);
        lengths.push(raw.length, disp.length);
      }
      const maxChars = lengths.length ? Math.max(...lengths) : 12;
      const titleLen = String(this.colorGroupDisplayName || key || "").length;
      const chars = Math.max(maxChars, Math.min(Math.max(titleLen, 8), 56));
      return Math.min(400, Math.max(96, Math.round(48 + chars * 6.4)));
    },
    generateLayout() {
      const hasLegends = this.chartType === "Histogram" 
        ? (this.hasValidXColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '')
        : this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0
          ? (this.colorGroupKey && this.colorGroupKey.trim() !== '')
          : (this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '');
      const rightMargin = hasLegends ? this.computeColorGroupLegendMargin() : 62;

      // Simple responsive layout
      const baseLayout = {
        autosize: true,
        height: this.chartHeight || 500,
        margin: { l: 62, r: rightMargin, b: 62, t: 56 },
        legend: {
          title: {
            text: hasLegends ? (this.colorGroupDisplayName || this.colorGroupKey) : '',
            font: { size: 13 }
          },
          visible: hasLegends,
          x: hasLegends ? 1 : 0.98,
          y: 0.5,
          orientation: 'v',
          xanchor: hasLegends ? 'left' : 'right',
          yanchor: 'middle',
          font: { size: 11 },
          tracegroupgap: 6,
          itemwidth: 30,
          borderwidth: 0,
          bgcolor: "rgba(0,0,0,0)",
        },
      };

      if (this.chartType === "3D Scatter") {
        return mergePlotlyTheme({
          ...baseLayout,
          scene: {
            xaxis: {
              title: { 
                text: this.formatAxisTitle(
                  this.xAxisDisplayName || '',
                  this.getUOMForProperty(this.xAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickangle: -45,
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.xAxisScale === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('x'),
              }),
            },
            yaxis: {
              title: {
                text: this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.yAxisScale === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('y'),
              }),
            },
            zaxis: {
              title: {
                text: this.formatAxisTitle(
                  this.zAxisDisplayName || 'Z',
                  this.getUOMForProperty(this.zAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              tickformat: this.getFormattedTickFormat('z'),
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 }
            },
            aspectmode: 'auto',
            aspectratio: { x: 1, y: 1, z: 1 }
          },
        }, this.isDarkMode);
      }

      const h2dUiRevision = this.chartType === "2D Histogram" ? `h2d-${this.chartName || "chart"}` : null;
      let h2dPaddedRanges = null;
      if (this.chartType === "2D Histogram" && this.xAxisScale !== "log" && this.yAxisScale !== "log") {
        const yRow = this.yAxisData && this.yAxisData[0];
        const ext = getHistogram2dExtentsFromAxisArrays(this.xAxisData, yRow);
        if (ext) {
          h2dPaddedRanges = padHistogram2dAxisRange(ext, 0.01);
        }
      }

      return mergePlotlyTheme({
        ...baseLayout,
        ...(h2dUiRevision ? { uirevision: h2dUiRevision } : {}),
        xaxis: {
          title: { 
            text: this.formatAxisTitle(
              this.xAxisDisplayName || '',
              this.getUOMForProperty(this.xAxisColumn)
            ),
              standoff: 30, // Increased standoff for better separation from scale
            font: { size: 12 }
          },
          tickangle: -45,
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.xAxisScale === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('x'),
          }),
          ...(h2dPaddedRanges ? { range: h2dPaddedRanges.xrange, autorange: false } : {}),
        },
        yaxis: {
          title: {
            text: (this.chartType === "Histogram")
              ? "No. of Samples"
              : (() => {
                  const displayName = this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : '';
                  const columnName = this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null;
                  const uom = this.getUOMForProperty(columnName);
                  return this.formatAxisTitle(displayName, uom);
                })(),
              standoff: 30, // Increased standoff for better separation from scale
            font: { size: 12 }
          },
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.yAxisScale === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('y'),
          }),
          ...(h2dPaddedRanges ? { range: h2dPaddedRanges.yrange, autorange: false } : {}),
        },
        margin: { l: 64, r: rightMargin, b: 74, t: 58 },
        height: this.chartHeight,
        autosize: true,
        ...(this.chartType === "Histogram" ? {
          plot_bgcolor: this.isDarkMode ? 'transparent' : '#e5ecf6',
          paper_bgcolor: this.isDarkMode ? 'transparent' : '#ffffff',
          margin: { l: 64, r: rightMargin, b: 58, t: 58 },
          height: this.chartHeight,
        } : {}),
        legend: {
          ...baseLayout.legend,
          x: hasLegends ? 1 : 0.98,
          tracegroupgap: 6,
          itemwidth: 32,
          borderwidth: 0,
          bgcolor: "rgba(0,0,0,0)",
          font: { size: 11 },
          title: {
            ...baseLayout.legend.title,
            font: { size: 13 },
          },
        },
      }, this.isDarkMode);
    },
    generateModalLayout() {
      const hasLegends = this.chartType === "Histogram" 
        ? (this.hasValidXColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '')
        : this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0
          ? (this.colorGroupKey && this.colorGroupKey.trim() !== '')
          : (this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '');
      const rightMargin = hasLegends ? this.computeColorGroupLegendMargin() : 62;

      // Simple responsive layout for modal
      const baseLayout = {
        autosize: true,
        height: this.calculateModalChartHeight() || 600,
        margin: { l: 62, r: rightMargin, b: 62, t: 56 },
        legend: {
          title: {
            text: hasLegends ? (this.colorGroupDisplayName || this.colorGroupKey) : '',
            font: { size: 13 }
          },
          visible: hasLegends,
          x: hasLegends ? 1 : 0.98,
          y: 0.5,
          orientation: 'v',
          xanchor: hasLegends ? 'left' : 'right',
          yanchor: 'middle',
          font: { size: 11 },
          tracegroupgap: 6,
          itemwidth: 30,
          borderwidth: 0,
          bgcolor: "rgba(0,0,0,0)",
        },
      };

      if (this.chartType === "3D Scatter") {
        return mergePlotlyTheme({
          ...baseLayout,
          scene: {
            xaxis: {
              title: { 
                text: this.formatAxisTitle(
                  this.xAxisDisplayName || '',
                  this.getUOMForProperty(this.xAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickangle: -45,
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.xAxisScaleModal === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('x'),
              }),
            },
            yaxis: {
              title: {
                text: this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.yAxisScaleModal === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('y'),
              }),
            },
            zaxis: {
              title: {
                text: this.formatAxisTitle(
                  this.zAxisDisplayName || 'Z',
                  this.getUOMForProperty(this.zAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              tickformat: this.getFormattedTickFormat('z'),
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 }
            },
            aspectmode: 'auto',
            aspectratio: { x: 1, y: 1, z: 1 }
          },
        }, this.isDarkMode);
      }

      const h2dModalUiRevision = this.chartType === "2D Histogram" ? `h2d-modal-${this.chartName || "chart"}` : null;
      let h2dModalPaddedRanges = null;
      if (this.chartType === "2D Histogram" && this.xAxisScaleModal !== "log" && this.yAxisScaleModal !== "log") {
        const yRow = this.yAxisData && this.yAxisData[0];
        const ext = getHistogram2dExtentsFromAxisArrays(this.xAxisData, yRow);
        if (ext) {
          h2dModalPaddedRanges = padHistogram2dAxisRange(ext, 0.01);
        }
      }

      return mergePlotlyTheme({
        ...baseLayout,
        ...(h2dModalUiRevision ? { uirevision: h2dModalUiRevision } : {}),
        xaxis: {
          title: { 
            text: this.formatAxisTitle(
              this.xAxisDisplayName || '',
              this.getUOMForProperty(this.xAxisColumn)
            ),
            standoff: 30,
            font: { size: 12 }
          },
          tickangle: -45,
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.xAxisScaleModal === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('x'),
          }),
          ...(h2dModalPaddedRanges ? { range: h2dModalPaddedRanges.xrange, autorange: false } : {}),
        },
        yaxis: {
          title: {
            text: (this.chartType === "Histogram")
              ? "No. of Samples"
              : this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
            standoff: 30,
            font: { size: 12 }
          },
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.yAxisScaleModal === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('y'),
          }),
          ...(h2dModalPaddedRanges ? { range: h2dModalPaddedRanges.yrange, autorange: false } : {}),
        },
        ...(this.chartType === "Histogram" ? {
          plot_bgcolor: this.isDarkMode ? 'transparent' : '#e5ecf6',
          paper_bgcolor: this.isDarkMode ? 'transparent' : '#ffffff',
        } : {}),
      }, this.isDarkMode);
    },
    applyStudioGridTheme(chartRef) {
      const el = chartRef || this.$refs[this.chartId];
      if (el) {
        applyPlotlyGridLineStroke(el, this.isDarkMode);
      }
    },
    generateConfig() {
      return {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d', "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadPNG,
          },
          {
            name: "Expand",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${Expand}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.openModal,
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadCSV,
          },
        ],
        autosize: true,
        useResizeHandler: true,
        maintainAspectRatio: true,
      };
    },
    generateModalConfig() {
      return {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d', "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadPNG,
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadCSV,
          },
        ],
        autosize: true,
        useResizeHandler: true,
        maintainAspectRatio: true,
      };
    },
    getTraceType() {
      switch (this.chartType) {
        case "Line Plot": return "scatter";
        case "Bar Chart": return "bar";
        case "Histogram": return "histogram";
        case "Bubble": return "scatter";
        case "3D Scatter": return "scatter3d";
        case "Scatter Plot": return "scatter";
        case "2D Histogram": return "histogram2d";
        case "Heatmap": return "heatmap";
        case "Treemap": return "treemap";
        default: return "scatter";
      }
    },
    getTraceMode() {
      switch (this.chartType) {
        case "Line Plot": 
          return "lines";
        case "Bubble": return "markers";
        case "Scatter Plot": return "markers";
        case "3D Scatter": return "markers";
        case "Bar Chart": return undefined;
        case "Histogram": return undefined;
        case "2D Histogram": return undefined;
        case "Heatmap": return undefined;
        case "Treemap": return undefined;
        default: return "lines";
      }
    },
    getLayoutTitle() {
      const titles = {
        "Line Plot": "Line Plot",
        "Bar Plot": "Bar Plot",
        "Histogram": "Histogram Plot",
        "Bubble": "Bubble Plot",
        "3D Scatter": "3D Scatter Plot",
        "Scatter Plot": "Scatter Plot",
        "2D Histogram": "2D Histogram Plot",
        "Heatmap": "Heatmap Plot",
        "Treemap": "Treemap Plot"
      };
      return titles[this.chartType] || "";
    },
    calculateBinSize(data, bins) {
      if (!data || data.length === 0 || !bins || bins < 1) {
        return 1;
      }
      
      const validData = data.filter(value => 
        value !== null && 
        value !== undefined && 
        value !== ''
      );
      
      if (validData.length === 0) {
        return 1;
      }
      
      const numericData = validData.filter(value => typeof value === 'number' && !isNaN(value));
      if (numericData.length > 0) {
        const minValue = Math.min(...numericData);
        const maxValue = Math.max(...numericData);
        const range = maxValue - minValue;
        
        if (range === 0) {
          return 1;
        }
        
        const binSize = range / bins;
        
        return binSize;
      }
      
      const uniqueValues = [...new Set(validData)];
      const uniqueCount = uniqueValues.length;
      
      const optimalBins = Math.min(bins, uniqueCount);
      
      return Math.max(uniqueCount / optimalBins, 1);
    },
    
    detectPropagationPattern(xData, yData) {
      if (!xData || !yData || xData.length !== yData.length || xData.length < 2) {
        return { type: 'unknown', confidence: 0 };
      }
      
      const validPairs = [];
      for (let i = 0; i < xData.length; i++) {
        const x = xData[i];
        const y = yData[i];
        if (x !== null && x !== undefined && x !== '' && 
            y !== null && y !== undefined && y !== '' && 
            !isNaN(y) && typeof x === 'number') {
          validPairs.push({ x, y });
        }
      }
      
      if (validPairs.length < 2) {
        return { type: 'unknown', confidence: 0 };
      }
      
      validPairs.sort((a, b) => {
        if (typeof a.x === 'number' && typeof b.x === 'number') {
          return a.x - b.x;
        }
        if (typeof a.x === 'string' && typeof b.x === 'string') {
          return a.x.localeCompare(b.x);
        }
        return String(a.x).localeCompare(String(b.x));
      });
      
      let increasingCount = 0;
      let decreasingCount = 0;
      
      for (let i = 1; i < validPairs.length; i++) {
        const prevY = validPairs[i - 1].y;
        const currY = validPairs[i].y;
        
        if (currY > prevY) {
          increasingCount++;
        } else if (currY < prevY) {
          decreasingCount++;
        }
      }
      
      const totalComparisons = validPairs.length - 1;
      const increasingRatio = increasingCount / totalComparisons;
      const decreasingRatio = decreasingCount / totalComparisons;
      
      if (increasingRatio > 0.7) {
        return { type: 'increasing', confidence: increasingRatio };
      } else if (decreasingRatio > 0.7) {
        return { type: 'decreasing', confidence: decreasingRatio };
      } else {
        return { type: 'fluctuating', confidence: Math.max(increasingRatio, decreasingRatio) };
      }
    },
    openModal() {
      this.isModalOpen = true;
      
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.plotModalChart();
        });
      });
    },
    closeModal() {
      this.isModalOpen = false;
    },
    
    

    checkSidebarState() {
      const currentSidebarState = localStorage.getItem('sidebarState');
      if (this.previousSidebarState !== currentSidebarState) {
        this.previousSidebarState = currentSidebarState;
      }
    },
    setupsidebarStateCheck() {
      this.checkSidebarState();
      this.sidebarStateCheckInterval = setInterval(this.checkSidebarState, 100);
    },
    removeSidebarStateCheck() {
      if (this.sidebarStateCheckInterval) {
        clearInterval(this.sidebarStateCheckInterval);
      }
    },
    handleFilterDrawerChange() {
      this.$nextTick(() => {
        this.plotChart();
      });
    },
    resizePlotlyHost(ref) {
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (!el) return;
      Plotly.Plots.resize(el).catch(() => {});
    },
    handleResizeChart() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.resizePlotlyHost(this.$refs[this.chartId]);
          this.resizePlotlyHost(this.$refs[this.modalChartId]);
        }, 150);
      });
    },






    setupLegendClickHandler(isModal = false) {
      let chartRef = isModal ? this.$refs[this.modalChartId] : this.$refs[this.chartId];
      
      if (Array.isArray(chartRef)) {
        chartRef = chartRef[0];
      }
      
      if (!chartRef) {
        console.warn('Chart container not found for legend click handler', {
          isModal,
          modalChartId: this.modalChartId,
          chartId: this.chartId,
          refs: Object.keys(this.$refs)
        });
        return;
      }

      try {
        if (chartRef.removeAllListeners) {
          chartRef.removeAllListeners('plotly_legendclick');
        }
        
        if (chartRef.on) {
          chartRef.on('plotly_legendclick', (event) => {
            const clickedItem = event.curveNumber;
            const traces = event.data;
            
            if (clickedItem !== undefined && traces && traces[clickedItem]) {
              const clickedTrace = traces[clickedItem];
              
              const updates = {};
              updates[`visible[${clickedItem}]`] = !clickedTrace.visible;
              
              Plotly.restyle(chartRef, updates);
            }
          });
        } else {
          console.warn('Using fallback event handling for legend clicks');
          const chartElement = chartRef;
          if (chartElement && chartElement.addEventListener) {
            chartElement.addEventListener('plotly_legendclick', (event) => {
              const clickedItem = event.detail.curveNumber;
              const traces = event.detail.data;
              
              if (clickedItem !== undefined && traces && traces[clickedItem]) {
                const clickedTrace = traces[clickedItem];
                
                const updates = {};
                updates[`visible[${clickedItem}]`] = !clickedTrace.visible;
                
                Plotly.restyle(chartRef, updates);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error setting up legend click handler:', error);
      }
    },
    

  },
  mounted() {
    this.previousSidebarState = localStorage.getItem('sidebarState');
    
    this.$nextTick(() => {
      this.plotChart();
      
      this.detectEditToCreateTransition();
    });
    
    eventBus.on("filterDrawerToggled", this.handleFilterDrawerChange);
    eventBus.on("resizeChart", this.handleResizeChart);
  },
  beforeUnmount() {
    this.removeSidebarStateCheck();

    eventBus.off("filterDrawerToggled", this.handleFilterDrawerChange);
    eventBus.off("resizeChart", this.handleResizeChart);
  },
};
</script>

<style scoped>
/* Smooth height transitions for edit mode */
.studio-chart-wrapper.create-edit {
  transition: height 0.4s ease-out;
}


/* Ensure the modal chart container can accommodate the calculated dimensions */
.modal-chart-container {
  display: block;
  width: 100% !important;
  height: 100% !important;
  min-height: 400px;
  position: relative;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
  box-sizing: border-box;
  /* Ensure maximum space utilization */
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  /* Force full space usage */
  min-width: 100%;
  min-height: 100%;
}

/* Ensure Plotly container is visible and properly sized */
.modal-chart-container .js-plotly-plot {
  width: 100% !important;
  height: 100% !important;
  min-width: 300px;
  min-height: 300px;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
}

/* Ensure the plotly container itself is visible */
.modal-chart-container .plotly {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
}

/* Remove any default spacing from plotly elements */
.modal-chart-container .plotly .main-svg {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

/* Force plotly to use full container space */
.modal-chart-container .plotly .plot-container {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Responsive modal chart styles */
@media (max-width: 1200px) {
  .modal-chart-container {
    min-height: 450px;
  }
}

@media (max-width: 768px) {
  .modal-chart-container {
    min-height: 400px;
  }
}

@media (max-width: 480px) {
  .modal-chart-container {
    min-height: 350px;
  }
}
</style>


